import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import "./CSS/Achievements.css";

const BADGES = [
  {
    id: "uploader10",
    img: "/assets/badges/videoBadge.png",
    name: "Video Apprentice",
    desc: "Upload 2 videos",
    rule: (s) => s.videos >= 2,
  },
  {
    id: "uploader25",
    img: "/assets/badges/videoBadgePro.png",
    name: "Video Pro",
    desc: "Upload 5 videos",
    rule: (s) => s.videos >= 5,
  },
  {
    id: "time500",
    img: "/assets/badges/timeMaster.png",
    name: "Time Master",
    desc: "Spend 30 minutes",
    rule: (s) => s.minutes >= 30,
  },
  {
    id: "q50",
    img: "/assets/badges/curiousMind.png",
    name: "Curious Mind",
    desc: "Send 20 messages",
    rule: (s) => s.messages >= 20,
  },
  {
    id: "streak7",
    img: "/assets/badges/consistency.png",
    name: "Consistency Hero",
    desc: "3-day practice streak",
    rule: (s) => s.streak >= 3,
  },
];

const Confetti = () => {
  return (
    <div className="confetti-container">
      {[...Array(20)].map((_, i) => (
        <motion.div
          key={i}
          className="confetti-piece"
          initial={{
            top: "50%",
            left: "50%",
            opacity: 1,
            scale: 0,
          }}
          animate={{
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            opacity: 0,
            scale: 2,
          }}
          transition={{
            duration: 1.5,
            delay: Math.random() * 0.5,
            repeat: Infinity,
            repeatDelay: 3,
          }}
          style={{
            backgroundColor: [
              "#FF4A8D",
              "#6C63FF",
              "#4ECDC4",
              "#FFD166",
              "#FF7D54",
            ][Math.floor(Math.random() * 5)],
          }}
        />
      ))}
    </div>
  );
};

const Badge = ({ badge, earned }) => {
  const [hover, setHover] = useState(false);

  return (
    <motion.div
      className={`badge ${earned ? "earned" : "locked"}`}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 300 }}
      onHoverStart={() => setHover(true)}
      onHoverEnd={() => setHover(false)}
    >
      <div className="badge-icon-container">
        {earned && hover && (
          <motion.div
            className="badge-pulse"
            initial={{ scale: 0 }}
            animate={{ scale: 1.5 }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            style={{
              background: `radial-gradient(circle, ${badge.color}10 0%, transparent 70%)`,
              opacity: 0.6,
            }}
          />
        )}
        {badge.img ? (
          <img src={badge.img} alt={badge.name} className="badge-image" />
        ) : (
          <span className="badge-emoji-large">{badge.emoji}</span>
        )}
      </div>

      <strong className="badge-name">{badge.name}</strong>
      <p className="badge-description">{badge.desc}</p>

      {!earned && (
        <div className="lock-overlay">
          <motion.div className="lock-icon-container lock-pulse">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="lock-icon"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                clipRule="evenodd"
              />
            </svg>
          </motion.div>
        </div>
      )}
    </motion.div>
  );
};

export default function Achievements({ stats = {} }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
      document.documentElement.style.overflow = prevHtmlOverflow;
    };
  }, [open]);

  const earned = BADGES.filter((b) => b.rule(stats));
  const locked = BADGES.filter((b) => !b.rule(stats));
  const progressPercentage = Math.round((earned.length / BADGES.length) * 100);

  return (
    <div className="achievements-container">
      <motion.button
        type="button"
        className="achievement-ribbon upgrade glow"
        onClick={() => setOpen(true)}
        whileHover={{ y: -2 }}
        whileTap={{ scale: 0.97 }}
      >
        <div className="ribbon-donut">
          <svg viewBox="0 0 36 36">
            <circle className="track" cx="18" cy="18" r="16" />
            <motion.circle
              className="fill"
              cx="18"
              cy="18"
              r="16"
              strokeDasharray="100,100"
              initial={{ strokeDasharray: "0 100" }}
              animate={{ strokeDasharray: `${progressPercentage} 100` }}
              transition={{ duration: 1 }}
            />
          </svg>
          <span className="donut-text">{earned.length}</span>
        </div>

        <span className="badge-emoji">🏅</span>

        <div className="ribbon-copy">
          <strong>
            {earned.length} / {BADGES.length}
          </strong>
          <span className="sub">badges unlocked</span>
        </div>

        <svg className="chevron" viewBox="0 0 20 20" fill="none">
          <path
            d="M6 8l4 4 4-4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </motion.button>

      {createPortal(
        <AnimatePresence>
          {open && (
          <motion.div
            className="modal-overlay-rewards"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
          >
            <motion.div
              className="achievements-modal"
              initial={{ scale: 0.85, y: 50 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.85, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-header-light" />
                <div className="modal-header-content">
                  <div>
                    <h3 className="modal-title">My Achievements</h3>
                    <p className="modal-subtitle">Keep up the great work!</p>
                  </div>
                  <button
                    className="modal-close-button"
                    onClick={() => setOpen(false)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="modal-close-icon"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>

                <div className="progress-container">
                  <div className="progress-scale">
                    <motion.div
                      className="progress-fill"
                      initial={{ width: 0 }}
                      animate={{ width: `${progressPercentage}%` }}
                      transition={{ delay: 0.5, duration: 1 }}
                    />
                  </div>
                  <span className="progress-percent">
                    {progressPercentage}%
                  </span>
                </div>
              </div>

              <div className="modal-body">
                <section>
                  <div className="section-header">
                    <h4 className="section-title">Earned Badges</h4>
                    <span className="badge-counter">
                      {earned.length}/{BADGES.length}
                    </span>
                  </div>

                  {earned.length ? (
                    <div className="badge-grid">
                      {earned.map((badge) => (
                        <Badge key={badge.id} badge={badge} earned={true} />
                      ))}
                      {earned.length > 0 && <Confetti />}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p className="empty-message">
                        No badges yet — keep going! 💪
                      </p>
                    </div>
                  )}
                </section>

                {locked.length > 0 && (
                  <section className="mt-8">
                    <h4 className="section-title mb-4">Next to Unlock</h4>
                    <div className="badge-grid">
                      {locked.map((badge) => (
                        <Badge key={badge.id} badge={badge} earned={false} />
                      ))}
                    </div>
                  </section>
                )}
              </div>

              <div className="modal-footer">
                <button
                  className="continue-button"
                  onClick={() => setOpen(false)}
                >
                  Continue My Journey
                </button>
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
